import { create } from 'zustand'

export type CopilotSpeaker = 'rep' | 'customer'

export interface CopilotTranscriptLine {
  id: string
  call_sid: string
  speaker: CopilotSpeaker
  text: string
  ts: number
}

export interface CopilotSentiment {
  score: number
  evidence: string
  ts: number
}

export interface CopilotInsight {
  id: string
  category: string
  summary: string
  evidence?: string
  ts: number
  dismissed?: boolean
}

export interface CopilotCard {
  id: string
  kind: string
  title: string
  body: string
  priority: number
  ts: number
  dismissed?: boolean
  snoozedUntil?: number
}

export interface CopilotCallbackProposal {
  id: string
  callback_at: string
  callback_reason: string
  confidence: number
  needs_review: boolean
  ts: number
  dismissed?: boolean
  approved?: boolean
}

interface PerCallState {
  transcript: CopilotTranscriptLine[]
  sentiment: CopilotSentiment | null
  sentimentHistory: CopilotSentiment[]
  insights: CopilotInsight[]
  cards: CopilotCard[]
  callback: CopilotCallbackProposal | null
  recordingUrl?: string
}

interface CopilotState {
  byCallSid: Record<string, PerCallState>
  handleEvent: (eventType: string, data: { call_sid?: string; timestamp?: number; payload?: Record<string, unknown> }) => void
  hydrate: (
    callSid: string,
    events: Array<{ event_type: string; timestamp?: number; payload?: Record<string, unknown> }>,
  ) => void
  reset: (callSid: string) => void
  dismissInsight: (callSid: string, id: string) => void
  dismissCard: (callSid: string, id: string) => void
  snoozeCard: (callSid: string, id: string, ms: number) => void
  dismissCallback: (callSid: string) => void
  markCallbackApproved: (callSid: string) => void
}

const MAX_TRANSCRIPT = 500

const empty = (): PerCallState => ({
  transcript: [],
  sentiment: null,
  sentimentHistory: [],
  insights: [],
  cards: [],
  callback: null,
})

// Pure per-event reducer shared by the live `handleEvent` path and the
// post-call `hydrate` replay. Returns the unchanged slice for events it
// doesn't recognize or that carry no usable data.
function reduceEvent(
  per: PerCallState,
  callSid: string,
  eventType: string,
  ts: number,
  payload: Record<string, unknown>,
): PerCallState {
  const str = (key: string): string => {
    const v = payload[key]
    return typeof v === 'string' ? v : ''
  }
  const num = (key: string, fallback = 0): number => {
    const v = payload[key]
    const n = typeof v === 'number' ? v : Number(v)
    return Number.isFinite(n) ? n : fallback
  }

  switch (eventType) {
    case 'copilot.transcript.rep':
    case 'copilot.transcript.customer': {
      const speaker: CopilotSpeaker =
        eventType === 'copilot.transcript.rep' ? 'rep' : 'customer'
      const text = str('text').trim()
      if (!text) return per
      const line: CopilotTranscriptLine = {
        id: `${callSid}-${ts}-${speaker}-${per.transcript.length}`,
        call_sid: callSid,
        speaker,
        text,
        ts,
      }
      return {
        ...per,
        transcript: [...per.transcript, line].slice(-MAX_TRANSCRIPT),
      }
    }
    case 'copilot.sentiment': {
      const reading: CopilotSentiment = {
        score: num('score'),
        evidence: str('evidence'),
        ts,
      }
      return {
        ...per,
        sentiment: reading,
        sentimentHistory: [...per.sentimentHistory, reading],
      }
    }
    case 'copilot.insight': {
      const insight: CopilotInsight = {
        id: `${callSid}-insight-${ts}-${per.insights.length}`,
        category: str('category') || 'other',
        summary: str('summary'),
        evidence: str('evidence'),
        ts,
      }
      return { ...per, insights: [...per.insights, insight] }
    }
    case 'copilot.card': {
      const card: CopilotCard = {
        id: `${callSid}-card-${ts}-${per.cards.length}`,
        kind: str('kind') || 'question',
        title: str('title'),
        body: str('body'),
        priority: num('priority', 1),
        ts,
      }
      return { ...per, cards: [...per.cards, card] }
    }
    case 'copilot.recording_available': {
      return { ...per, recordingUrl: str('recording_url') || per.recordingUrl }
    }
    case 'copilot.callback_proposed': {
      const callbackAt = str('callback_at')
      if (!callbackAt) return per
      const proposal: CopilotCallbackProposal = {
        id: `${callSid}-callback-${ts}`,
        callback_at: callbackAt,
        callback_reason: str('callback_reason'),
        confidence: num('confidence'),
        needs_review: payload['needs_review'] === true,
        ts,
      }
      return { ...per, callback: proposal }
    }
    default:
      return per
  }
}

export const useCopilotStore = create<CopilotState>((set) => ({
  byCallSid: {},

  handleEvent: (eventType, data) => {
    const callSid = data.call_sid
    if (!callSid) return
    const payload: Record<string, unknown> = data.payload ?? {}
    const ts: number = data.timestamp ?? Date.now() / 1000

    set((state) => {
      const per = state.byCallSid[callSid] ?? empty()
      const next = reduceEvent(per, callSid, eventType, ts, payload)
      if (next === per) return state
      return { byCallSid: { ...state.byCallSid, [callSid]: next } }
    })
  },

  hydrate: (callSid, events) => {
    if (!callSid) return
    set((state) => {
      let per = empty()
      for (const event of events) {
        const eventType = event.event_type
        if (!eventType.startsWith('copilot.')) continue
        const ts = event.timestamp ?? 0
        per = reduceEvent(per, callSid, eventType, ts, event.payload ?? {})
      }
      return { byCallSid: { ...state.byCallSid, [callSid]: per } }
    })
  },

  reset: (callSid) =>
    set((state) => {
      const next = { ...state.byCallSid }
      delete next[callSid]
      return { byCallSid: next }
    }),

  dismissInsight: (callSid, id) =>
    set((state) => {
      const per = state.byCallSid[callSid]
      if (!per) return state
      return {
        byCallSid: {
          ...state.byCallSid,
          [callSid]: {
            ...per,
            insights: per.insights.map((i) =>
              i.id === id ? { ...i, dismissed: true } : i,
            ),
          },
        },
      }
    }),

  dismissCard: (callSid, id) =>
    set((state) => {
      const per = state.byCallSid[callSid]
      if (!per) return state
      return {
        byCallSid: {
          ...state.byCallSid,
          [callSid]: {
            ...per,
            cards: per.cards.map((c) =>
              c.id === id ? { ...c, dismissed: true } : c,
            ),
          },
        },
      }
    }),

  snoozeCard: (callSid, id, ms) =>
    set((state) => {
      const per = state.byCallSid[callSid]
      if (!per) return state
      return {
        byCallSid: {
          ...state.byCallSid,
          [callSid]: {
            ...per,
            cards: per.cards.map((c) =>
              c.id === id ? { ...c, snoozedUntil: Date.now() + ms } : c,
            ),
          },
        },
      }
    }),

  dismissCallback: (callSid) =>
    set((state) => {
      const per = state.byCallSid[callSid]
      if (!per?.callback) return state
      return {
        byCallSid: {
          ...state.byCallSid,
          [callSid]: { ...per, callback: { ...per.callback, dismissed: true } },
        },
      }
    }),

  markCallbackApproved: (callSid) =>
    set((state) => {
      const per = state.byCallSid[callSid]
      if (!per?.callback) return state
      return {
        byCallSid: {
          ...state.byCallSid,
          [callSid]: { ...per, callback: { ...per.callback, approved: true } },
        },
      }
    }),
}))
